import { useCallback } from 'react';
import { getIn } from 'formik';

import { FRadioGroup } from '../../../../components/baseV2';
import { useFormReadOnly } from '../../../common';
import { REQUIRED_FIELD_ERROR_SUFFIX } from '../constants';

import type { FocusEvent, ChangeEvent } from 'react';
import type { FormikValues } from 'formik';
import type { FormRadioGroupFieldProps } from '../types';

export default function FormRadioGroupField<TValues extends FormikValues>({
  formik,
  name,
  label,
  options,
  required = false,
  disabled = false,
  readOnly = false,
  onBlur,
  onChange,
}: FormRadioGroupFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  const errorMessage = isTouched ? fieldError?.toString() : undefined;
  // Suppress only the redundant "required field" message for an empty required field; the field still
  // highlights via `error` and its required indicator. Any other validation message still shows.
  const helperText =
    required && !getIn(formik.values, name) && errorMessage?.endsWith(REQUIRED_FIELD_ERROR_SUFFIX)
      ? undefined
      : errorMessage;
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      formik.handleBlur(event);
      onBlur?.(name);
    },
    [formik, name, onBlur],
  );
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      formik.handleChange(event);
      onChange?.(name, event.target.value);
    },
    [formik, name, onChange],
  );

  return (
    <FRadioGroup
      disabled={disabled || readOnly || readOnlyFromContext}
      error={Boolean(errorMessage)}
      fullWidth
      helperText={helperText}
      label={label}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      options={options}
      required={required}
      row
      value={getIn(formik.values, name) ?? ''}
    />
  );
}
